/**
 * Policy page chrome + faithful body translations for the plain
 * consumer-information policy pages (shipping, refund, return/exchange,
 * privacy). Legally dense contract pages (Terms of Service, Affiliate
 * Partner Agreement, TruCoins Programme Terms) keep their English body
 * verbatim, only their shared chrome is translated, to avoid any risk of
 * legal-meaning drift.
 */

export const policies = {
  en: {
    "pol.eyebrow": "TruWe \u00b7 Policies",
    "pol.lastUpdated": "Last updated:",
    "pol.allPolicies": "All policies",
    "pol.navShipping": "Shipping Policy",
    "pol.navRefund": "Refund Policy",
    "pol.navReturnExchange": "Return & Exchange",
    "pol.navPrivacy": "Privacy Policy",
    "pol.navTerms": "Terms of Service",
    "pol.navTrucoins": "TruCoins Programme Terms",
    "pol.navAffiliate": "Affiliate Partner Agreement (draft)",
    "pol.needHelpTitle": "Need help?",
    "pol.needHelpBody": "Email us at {email}, we usually reply within one business day.",
    "pol.bilingualNote":
      "This page is also available in English; the English version prevails in case of any difference.",

    "pol.privacy.title": "Privacy Policy",
    "pol.privacy.intro":
      'TruWe ("TruWe", "we", "us", "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains what data we collect, how we use it, and the choices you have. By using truwe.in, you consent to this policy.',
    "pol.privacy.h1": "1. Information we collect",
    "pol.privacy.h1.identity": "Identity & contact details: name, email, phone, billing and shipping addresses.",
    "pol.privacy.h1.order": "Order information: products purchased, payment status, order history.",
    "pol.privacy.h1.payment":
      "Payment information: processed by our PCI-compliant payment gateway (Razorpay). We do not store card numbers or CVVs.",
    "pol.privacy.h1.wellness":
      "Wellness preferences (optional): answers you provide in our quiz to recommend products.",
    "pol.privacy.h1.device":
      "Device & usage data: IP address, browser, device type, pages viewed, referral source, cookies and similar technologies.",
    "pol.privacy.h2": "2. How we use your information",
    "pol.privacy.h2.orders": "To process orders, payments, shipping and customer support.",
    "pol.privacy.h2.transactional":
      "To send transactional messages (order updates, delivery tracking, refunds).",
    "pol.privacy.h2.improve": "To improve the site, our products and the shopping experience.",
    "pol.privacy.h2.marketing":
      "To send marketing communications, only if you have opted in. You can unsubscribe any time.",
    "pol.privacy.h2.fraud": "To detect fraud, abuse and ensure security.",
    "pol.privacy.h2.law": "To comply with applicable Indian law.",
    "pol.privacy.h3": "3. Sharing of information",
    "pol.privacy.h3.intro": "We share data only with trusted partners necessary to operate the business:",
    "pol.privacy.h3.payment": "Payment gateways (e.g. Razorpay) for processing payments.",
    "pol.privacy.h3.logistics":
      "Logistics partners (Shiprocket, Delhivery, Bluedart, DTDC, etc.) to deliver orders.",
    "pol.privacy.h3.cloud": "Cloud, analytics and email service providers acting on our instructions.",
    "pol.privacy.h3.govt": "Government authorities when required by law.",
    "pol.privacy.h3.noSell": "We do not sell or rent your personal data to third parties.",
    "pol.privacy.h4": "4. Cookies",
    "pol.privacy.h4.body":
      "We use cookies and similar technologies to remember your cart, keep you logged in, measure traffic and improve performance. You can control cookies through your browser settings; disabling them may break parts of the site.",
    "pol.privacy.h5": "5. Data retention",
    "pol.privacy.h5.body":
      "We retain personal data only as long as needed for the purposes described above and to meet legal, tax and accounting obligations.",
    "pol.privacy.h6": "6. Security",
    "pol.privacy.h6.body":
      "We use industry-standard safeguards including HTTPS encryption, access controls and secure cloud infrastructure. No system is 100% secure, but we work hard to protect your information.",
    "pol.privacy.h7": "7. Your rights",
    "pol.privacy.h7.pre":
      "You can request access, correction, or deletion of your personal data, or withdraw marketing consent, by emailing",
    "pol.privacy.h7.post": "We will respond within a reasonable timeframe as required by applicable law.",
    "pol.privacy.h8": "8. Children's privacy",
    "pol.privacy.h8.body":
      "Our products are intended for adult purchasers. Minors should use the site only with the involvement of a parent or guardian. We do not knowingly collect personal data from children under 13.",
    "pol.privacy.h9": "9. Updates to this policy",
    "pol.privacy.h9.pre":
      "We may update this Privacy Policy from time to time. The latest version will always be available at",
    "pol.privacy.h10": "10. Contact",
    "pol.privacy.h10.pre": "For privacy questions, email",

    "pol.refund.title": "Refund Policy",
    "pol.refund.intro.pre":
      "We want you to feel confident in every TruWe purchase. This Refund Policy explains when refunds are issued and how they are processed. Read together with our",
    "pol.refund.h1": "1. When refunds are issued",
    "pol.refund.h1.intro": "You are eligible for a refund in the following cases:",
    "pol.refund.h1.damaged": "The product delivered is damaged, defective, expired, or tampered with.",
    "pol.refund.h1.wrong": "An incorrect product was shipped to you.",
    "pol.refund.h1.lost": "Your order was lost in transit and not delivered.",
    "pol.refund.h1.cancelled": "You cancelled a prepaid order before it was dispatched.",
    "pol.refund.h2": "2. Non-refundable cases",
    "pol.refund.h2.intro":
      "For health, safety and hygiene reasons, the following are not eligible for refund:",
    "pol.refund.h2.opened": "Opened, used, or partially consumed products (unless defective).",
    "pol.refund.h2.noApproval": "Products returned without prior approval from our support team.",
    "pol.refund.h2.late": "Requests raised more than 3 days after delivery.",
    "pol.refund.h2.finalSale": "Products purchased on final-sale or clearance.",
    "pol.refund.h3": "3. How to request a refund",
    "pol.refund.h3.pre": "Email",
    "pol.refund.h3.mid": "within",
    "pol.refund.h3.window": "3 days of delivery",
    "pol.refund.h3.with": "with:",
    "pol.refund.h3.orderId": "Your order ID",
    "pol.refund.h3.reason": "Reason for the refund",
    "pol.refund.h3.photos": "Clear photos / a short video of the product and outer packaging",
    "pol.refund.h3.response":
      "Our team will respond within 1-2 business days and confirm whether a reverse pickup, replacement or refund will be processed.",
    "pol.refund.h4": "4. Refund timelines",
    "pol.refund.h4.prepaid":
      "Prepaid orders (UPI / card / netbanking / wallet): 5-7 business days to the original payment method after approval.",
    "pol.refund.h4.cod":
      "Cash on Delivery orders: 5-7 business days via UPI / bank transfer once you share your bank details.",
    "pol.refund.h4.bank": "Bank processing times may add 2-4 additional business days depending on your issuer.",
    "pol.refund.h5": "5. Cancellations",
    "pol.refund.h5.pre":
      "Orders can be cancelled free of charge before they are dispatched. Once dispatched, the order falls under our Return & Exchange Policy. To cancel, email",
    "pol.refund.h5.post": "with your order ID as soon as possible.",
    "pol.refund.h6": "6. Shipping fees",
    "pol.refund.h6.body":
      "Original shipping fees (where charged) are non-refundable unless the refund is due to our error (wrong, damaged or defective item).",
    "pol.refund.h7": "7. Contact",
    "pol.refund.h7.pre": "Questions about a refund? Reach us at",

    "pol.returnExchange.title": "Return & Exchange Policy",
    "pol.returnExchange.intro":
      "At TruWe we stand behind every batch we ship. Because our products are ingestible nutraceuticals, returns are accepted only in specific cases listed below to protect the safety of all customers.",
    "pol.returnExchange.h1": "1. Eligibility for return or exchange",
    "pol.returnExchange.h1.intro": "You may request a return or exchange if:",
    "pol.returnExchange.h1.damaged": "The product is damaged, leaking, broken or tampered with on arrival.",
    "pol.returnExchange.h1.expired":
      "The product is expired or has less than 3 months to expiry at delivery.",
    "pol.returnExchange.h1.wrong": "You received the wrong product or wrong variant.",
    "pol.returnExchange.h1.defect":
      "There is a manufacturing defect (e.g. broken seal, missing batch printing).",
    "pol.returnExchange.h2": "2. Items that cannot be returned",
    "pol.returnExchange.h2.opened": "Products that have been opened, used or partially consumed (unless defective).",
    "pol.returnExchange.h2.noApproval": "Items returned without prior support approval.",
    "pol.returnExchange.h2.finalSale":
      "Products marked as final sale or purchased under a non-returnable promotion.",
    "pol.returnExchange.h2.late": "Requests raised more than 3 days after delivery.",
    "pol.returnExchange.h3": "3. How to raise a request",
    "pol.returnExchange.h3.step1.pre": "Email",
    "pol.returnExchange.h3.step1.post": "within 3 days of delivery.",
    "pol.returnExchange.h3.step2":
      "Include your order ID, reason for return / exchange, and 2-3 clear photos plus a short video of the product and the outer packaging.",
    "pol.returnExchange.h3.step3":
      "Our team reviews requests within 1-2 business days and confirms the next step (reverse pickup, replacement, store credit or refund).",
    "pol.returnExchange.h4": "4. Reverse pickup",
    "pol.returnExchange.h4.body":
      "Where available, we arrange a free reverse pickup from your address. In non-serviceable pincodes, you may need to self-ship the product to our warehouse and share the courier receipt, we will reimburse standard courier charges up to \u20b9150.",
    "pol.returnExchange.h5": "5. Exchanges",
    "pol.returnExchange.h5.body":
      "Exchanges are offered for the same SKU when the original was damaged or defective. If the same SKU is unavailable, we will issue a refund or store credit of equal value.",
    "pol.returnExchange.h6": "6. Refund processing",
    "pol.returnExchange.h6.pre":
      "Once the returned product is received and inspected (typically within 3-5 business days of pickup), we initiate a refund as per the",
    "pol.returnExchange.h7": "7. Contact",
    "pol.returnExchange.h7.pre": "For any return or exchange request, please email",

    "pol.shipping.title": "Shipping Policy",
    "pol.shipping.intro":
      'This Shipping Policy explains how TruWe ("TruWe", "we", "us") processes, packs and dispatches orders placed on truwe.in.',
    "pol.shipping.h1": "1. Order processing",
    "pol.shipping.h1.body1":
      "Orders are processed within 24-48 business hours of payment confirmation (Monday to Saturday, excluding public holidays). You will receive an order confirmation email immediately after checkout, followed by a dispatch email with tracking details.",
    "pol.shipping.h2": "2. Shipping partners",
    "pol.shipping.h2.body":
      "We ship pan-India through trusted logistics partners including Shiprocket, Delhivery, Bluedart, DTDC and Indian Post. The partner is selected based on serviceability of your pincode.",
    "pol.shipping.h3": "3. Delivery timelines",
    "pol.shipping.h3.metro": "Metro cities: 2-4 business days from dispatch",
    "pol.shipping.h3.tier": "Tier 2 / Tier 3 cities: 4-7 business days from dispatch",
    "pol.shipping.h3.remote": "Remote / North-East / J&K / island regions: 7-10 business days",
    "pol.shipping.h3.note":
      "Timelines are indicative. Occasional delays may occur due to weather, courier load, regional restrictions or unforeseen events.",
    "pol.shipping.h4": "4. Shipping charges",
    "pol.shipping.h4.free": "Free shipping on all prepaid orders above \u20b9999.",
    "pol.shipping.h4.flat": "Orders below \u20b9999 carry a flat shipping fee of \u20b949, shown at checkout.",
    "pol.shipping.h4.cod": "Cash on Delivery (where available) needs a ₹99 advance paid online, deducted from your order total; the balance is paid on delivery. No COD fee.",
    "pol.shipping.h5": "5. Tracking your order",
    "pol.shipping.h5.body":
      "Once dispatched, you will receive a tracking link via email and SMS. You can track the live status of your shipment using the courier's tracking portal.",
    "pol.shipping.h6": "6. Non-delivery, RTO & wrong addresses",
    "pol.shipping.h6.body":
      "Please double-check your address and phone number before placing an order. If a shipment is returned to us as undelivered (RTO) due to an incorrect address, repeated unavailability or refusal at delivery, we will reach out to confirm reshipment. Reshipment charges may apply for prepaid orders; COD orders that RTO are not eligible for refund of the COD fee.",
    "pol.shipping.h7": "7. Damaged or missing items",
    "pol.shipping.h7.pre":
      "If your parcel arrives damaged, tampered with, or with missing items, please email a photograph and short video of the unopened parcel and the contents to",
    "pol.shipping.h7.mid": "within",
    "pol.shipping.h7.window": "48 hours",
    "pol.shipping.h7.post":
      "of delivery. We will replace the item or initiate a refund per our Refund Policy.",
    "pol.shipping.h8": "8. International shipping",
    "pol.shipping.h8.body": "We currently ship only within India. International orders are not supported at this time.",
    "pol.shipping.h9": "9. Contact",
    "pol.shipping.h9.pre": "For shipping questions, write to",
    "pol.shipping.h9.post": "with your order ID.",
  },
} as const;
